import { useMemo, useState, useCallback, useEffect, type FC } from "react";
import {
  deleteDeviceGroup,
  useDeviceGroups,
} from "../stores/device_group_store";
import { useCertificatePolicies } from "../stores/certificate_policy_store";
import { useCertificates } from "../stores/certificate_store";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Pagination } from "../components/pagination";
import { useActiveCertCountMap } from "../lib/active_cert_counts";
import { modalManager } from "../lib/modal_store";
import { useCustomerId } from "../stores/customer_id_store";
import { UnassignedPlaceholder } from "../components/unassigned_placeholder";

export const DeviceGroups: FC = () => {
  const customerId = useCustomerId();
  const groups = useDeviceGroups();
  const policies = useCertificatePolicies();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const certificates = useCertificates();
  const groupKeyFn = useCallback(
    (c: { deviceGroupId: string }) => c.deviceGroupId,
    []
  );
  const certCountByGroup = useActiveCertCountMap(certificates, groupKeyFn);

  // Total cert count (all statuses) — used to prevent deletion
  const totalCertCountByGroup = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of certificates) {
      map.set(c.deviceGroupId, (map.get(c.deviceGroupId) ?? 0) + 1);
    }
    return map;
  }, [certificates]);

  const sortedGroups = useMemo(() => {
    return [...groups].sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [groups]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalItems = sortedGroups.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedGroups = sortedGroups.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  const openCreate = () => {
    modalManager.open("createGroup", { policies });
    if (searchParams.has("action")) {
      searchParams.delete("action");
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Handle ?action=create on mount
  useEffect(() => {
    if (searchParams.get("action") === "create") {
      openCreate();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!customerId) {
    return <UnassignedPlaceholder />;
  }

  return (
    <div className="vmx-page">
      <div className="vmx-page-header">
        <div>
          <div className="vmx-page-title">⊞ Device Groups</div>
          <div className="vmx-page-subtitle">
            Manage device groups and assign certificate policies to control
            device enrollment.
          </div>
        </div>
        <div className="vmx-page-actions">
          <button className="vmx-btn vmx-btn-primary" onClick={openCreate}>
            ＋ New Device Group
          </button>
        </div>
      </div>

      {sortedGroups.length === 0 ? (
        <div className="vmx-card">
          <div className="vmx-empty">
            <div className="vmx-empty-icon">⊞</div>
            <div className="vmx-empty-title">No device groups yet</div>
            <div className="vmx-empty-text">
              Create your first device group to start grouping and securing
              devices.
            </div>
            <button className="vmx-btn vmx-btn-primary" onClick={openCreate}>
              ＋ Create Device Group
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="vmx-table-wrapper vmx-card-table-wrapper">
            <table className="vmx-table vmx-card-table vmx-groups-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Certificate Policy</th>
                  <th>Certificates</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedGroups.map((g) => {
                  const policy = policies.find(
                    (p) => p.id === g.certificatePolicyId
                  );
                  return (
                    <tr
                      key={g.id}
                      onClick={() => {
                        modalManager.open("groupDetail", {
                          group: g,
                          policy,
                          onPolicyClick: () => {
                            if (policy) {
                              modalManager.open("policyDetail", { policy });
                            }
                          },
                        });
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <td
                        data-label="Name"
                        style={{
                          fontWeight: 600,
                          color: "var(--vmx-gray-800)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span>{g.name}</span>
                          {g.isDefault && (
                            <span className="vmx-badge vmx-badge-neutral">
                              Default
                            </span>
                          )}
                        </div>
                      </td>
                      <td data-label="Policy">
                        <button
                          className="vmx-btn vmx-btn-ghost vmx-btn-sm"
                          style={{
                            color: "var(--vmx-accent)",
                            fontWeight: 600,
                            padding: "2px 6px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (policy !== undefined) {
                              modalManager.open("policyDetail", { policy });
                            }
                          }}
                        >
                          {policy?.name ?? "—"}
                        </button>
                      </td>
                      <td data-label="Certificates">
                        <button
                          type="button"
                          className="vmx-btn vmx-btn-ghost vmx-btn-sm"
                          style={{
                            color: "var(--vmx-accent)",
                            fontWeight: 600,
                            padding: "2px 6px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/certificates?groupId=${g.id}`);
                          }}
                        >
                          {certCountByGroup.get(g.id) ?? 0}
                        </button>
                      </td>
                      <td data-label="Actions">
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            className="vmx-btn vmx-btn-ghost vmx-btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              modalManager.open("editPolicy", {
                                group: g,
                                policies,
                              });
                            }}
                          >
                            ⇆ Change Certificate Policy
                          </button>
                          <button
                            className="vmx-btn vmx-btn-ghost vmx-btn-sm"
                            style={{ color: "var(--vmx-danger)" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              modalManager.open("revokeAll", {
                                groupName: g.name,
                                certificates: certificates
                                  .filter(
                                    (c) =>
                                      c.deviceGroupId === g.id &&
                                      (c.status === "active" ||
                                        c.status === "expiring")
                                  )
                                  .map((c) => ({
                                    deviceId: c.deviceId,
                                    certvalidfrom: c.validFrom,
                                  })),
                                onComplete: () => {},
                              });
                            }}
                          >
                            Revoke All
                          </button>
                          {!g.isDefault &&
                            (totalCertCountByGroup.get(g.id) ?? 0) === 0 && (
                              <button
                                className="vmx-btn vmx-btn-ghost vmx-btn-sm vmx-delete-group-btn"
                                style={{ color: "var(--vmx-danger)" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  modalManager.open("deleteConfirm", {
                                    title: "Delete Device Group",
                                    message: (
                                      <p>
                                        Are you sure you want to delete{" "}
                                        <strong>{g.name}</strong>? All devices
                                        configured with this device group ID
                                        won't be able to request new
                                        certificates. This action cannot be
                                        undone.
                                      </p>
                                    ),
                                    onConfirm: async () => {
                                      await deleteDeviceGroup(g.id);
                                    },
                                  });
                                }}
                              >
                                <span className="vmx-delete-icon">✕</span>
                                <span className="vmx-delete-label">Delete</span>
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={safePage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </>
      )}
    </div>
  );
};
